import { router } from '@inertiajs/react'
import { useMemo, useState } from 'react'

function AvatarGroup({ users, max = 4 }) {
  if (!users || users.length === 0) return <div className="small text-muted">No views</div>

  const visible = users.slice(0, max)
  const hiddenCount = Math.max(0, users.length - visible.length)

  return (
    <div className="avatar-group d-inline-flex align-items-center">
      {visible.map((u) => (
        <span
          key={u.id}
          className="avatar avatar-xs"
          aria-label={u.full_name || u.email}
          data-bs-toggle="tooltip"
          data-bs-title={u.full_name || u.email}
        >
          <img
            src={u.avatar_url}
            className="avatar-img rounded-circle"
            alt={u.full_name || u.email}
          />
        </span>
      ))}
      {hiddenCount > 0 ? <span className="small text-muted ms-2">+ {hiddenCount} more</span> : null}
    </div>
  )
}

function AttributeRow({ label, value }) {
  return (
    <tr>
      <td className="text-body-secondary">{label}</td>
      <td className="text-end">{value || ''}</td>
    </tr>
  )
}

function formatBalance(value) {
  if (value == null) return '0'
  return Number(value).toFixed(2).replace(/\.00$/, '')
}

function TimelineRow({ item }) {
  if (item.kind === 'refill') {
    return (
      <tr>
        <td>{item.event_date_label}</td>
        <td>Refill</td>
        <td>{item.leave_type === 'sick' ? 'Sick Leave' : 'Vacation Leave'}</td>
        <td>{formatBalance(item.amount)}</td>
        <td>{item.created_by?.full_name || item.created_by?.email || ''}</td>
      </tr>
    )
  }

  return (
    <tr>
      <td>{item.range_label || item.event_date_label}</td>
      <td>Leave</td>
      <td>{item.leave_type === 'sick' ? 'Sick Leave' : 'Vacation Leave'}</td>
      <td>{item.total_days}</td>
      <td>{item.approved_by?.full_name || item.approved_by?.email || ''}</td>
    </tr>
  )
}

const WEEKDAY_HEADERS_SHORT = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function buildMonthCells(year, month, reportDateSet) {
  const firstDay = new Date(year, month - 1, 1)
  const lastDay = new Date(year, month, 0)
  const daysInMonth = lastDay.getDate()
  const startDayOfWeek = firstDay.getDay() // 0 = Sunday
  const offset = (startDayOfWeek + 6) % 7 // Monday-first offset
  const prevMonthLastDay = new Date(year, month - 1, 0).getDate()

  const cells = []
  for (let i = offset - 1; i >= 0; i--) {
    cells.push({ isPadding: true, dayNumber: prevMonthLastDay - i, isWeekend: false })
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    const dateObj = new Date(dateStr + 'T00:00:00')
    const dayOfWeek = dateObj.getDay()
    cells.push({
      isPadding: false,
      dayNumber: d,
      date: dateStr,
      hasReport: reportDateSet.has(dateStr),
      isToday: dateStr === new Date().toISOString().slice(0, 10),
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
    })
  }
  const totalCells = cells.length
  const rows = Math.ceil(totalCells / 7)
  const targetCells = rows * 7
  for (let i = 1; i <= targetCells - totalCells; i++) {
    cells.push({ isPadding: true, dayNumber: i, isWeekend: false })
  }
  return cells
}

function buildYearCells(year, reportDateSet) {
  const months = []
  for (let m = 1; m <= 12; m++) {
    months.push(buildMonthCells(year, m, reportDateSet))
  }
  return months
}

function MonthCalendar({ year, month, reportDateSet, onDateClick }) {
  const cells = useMemo(() => buildMonthCells(year, month, reportDateSet), [year, month, reportDateSet])

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '0.25rem',
      }}
    >
      {WEEKDAY_HEADERS_SHORT.map((h) => (
        <div key={h} className="text-center small text-muted">
          {h}
        </div>
      ))}
      {cells.map((cell, index) => {
        if (cell.isPadding) {
          return <div key={`pad-${index}`} className="text-center small text-muted py-1">{cell.dayNumber}</div>
        }
        return (
          <button
            key={cell.date}
            type="button"
            className={`btn btn-sm p-1 ${cell.hasReport ? 'btn-success' : 'btn-white'} ${cell.isToday ? 'border-primary' : ''} ${cell.isWeekend && !cell.hasReport ? 'bg-dark-subtle' : ''}`}
            onClick={() => onDateClick?.(cell.date)}
            title={cell.date}
          >
            {cell.dayNumber}
          </button>
        )
      })}
    </div>
  )
}

function YearHeatmap({ year, reportDateSet, onDateClick }) {
  const months = useMemo(() => buildYearCells(year, reportDateSet), [year, reportDateSet])

  return (
    <div className="d-flex flex-wrap gap-3 justify-content-center">
      {months.map((cells, monthIndex) => (
        <div key={monthIndex} style={{ width: '8.5rem' }}>
          <div className="small text-muted text-center mb-1">{MONTH_NAMES[monthIndex].slice(0, 3)}</div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '0.15rem',
            }}
          >
            {cells.map((cell, i) => {
              if (cell.isPadding) {
                return <div key={`pad-${monthIndex}-${i}`} style={{ width: '1rem', height: '1rem' }} />
              }
              return (
                <button
                  key={cell.date}
                  type="button"
                  className="border-0 p-0"
                  style={{
                    width: '1rem',
                    height: '1rem',
                    borderRadius: '2px',
                    backgroundColor: cell.hasReport
                      ? (cell.isWeekend ? '#157347' : '#198754')
                      : (cell.isWeekend ? '#dee2e6' : '#e9ecef'),
                    cursor: onDateClick ? 'pointer' : 'default',
                    border: cell.isToday ? '1px solid #0d6efd' : 'none',
                  }}
                  onClick={() => onDateClick?.(cell.date)}
                  title={cell.date}
                />
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function DailyReportCalendar({ dailyReportDates, dailyReports }) {
  const [year, setYear] = useState(new Date().getFullYear())
  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [view, setView] = useState('month') // 'month' | 'year'

  const reportDateSet = useMemo(() => new Set(dailyReportDates || []), [dailyReportDates])

  const reportMap = useMemo(() => {
    const m = new Map()
    ;(dailyReports || []).forEach((dr) => {
      m.set(dr.applicable_date, dr)
    })
    return m
  }, [dailyReports])

  function scrollToDate(dateStr) {
    const el = document.getElementById(`daily-report-${dateStr}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.classList.add('border-primary')
      setTimeout(() => el.classList.remove('border-primary'), 2000)
    }
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h4 className="card-header-title">Daily Reports Calendar</h4>
        <div className="btn-group btn-group-sm">
          <button
            type="button"
            className={`btn ${view === 'month' ? 'btn-primary' : 'btn-white'}`}
            onClick={() => setView('month')}
          >
            Month
          </button>
          <button
            type="button"
            className={`btn ${view === 'year' ? 'btn-primary' : 'btn-white'}`}
            onClick={() => setView('year')}
          >
            Full Year
          </button>
        </div>
      </div>
      <div className="card-body">
        {view === 'month' ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <button
                type="button"
                className="btn btn-sm btn-white"
                onClick={() => {
                  if (month === 1) { setMonth(12); setYear(year - 1) } else { setMonth(month - 1) }
                }}
              >
                &larr;
              </button>
              <h5 className="m-0">{MONTH_NAMES[month - 1]} {year}</h5>
              <button
                type="button"
                className="btn btn-sm btn-white"
                onClick={() => {
                  if (month === 12) { setMonth(1); setYear(year + 1) } else { setMonth(month + 1) }
                }}
              >
                &rarr;
              </button>
            </div>
            <MonthCalendar
              year={year}
              month={month}
              reportDateSet={reportDateSet}
              onDateClick={scrollToDate}
            />
          </>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <button
                type="button"
                className="btn btn-sm btn-white"
                onClick={() => setYear(year - 1)}
              >
                &larr;
              </button>
              <h5 className="m-0">{year}</h5>
              <button
                type="button"
                className="btn btn-sm btn-white"
                onClick={() => setYear(year + 1)}
              >
                &rarr;
              </button>
            </div>
            <YearHeatmap
              year={year}
              reportDateSet={reportDateSet}
              onDateClick={scrollToDate}
            />
            <div className="d-flex align-items-center gap-2 mt-3 justify-content-center">
              <span className="small text-muted">Less</span>
              <div className="d-flex gap-1">
                <div style={{ width: '0.75rem', height: '0.75rem', borderRadius: '2px', backgroundColor: '#e9ecef' }} />
                <div style={{ width: '0.75rem', height: '0.75rem', borderRadius: '2px', backgroundColor: '#198754' }} />
              </div>
              <span className="small text-muted">More</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default function Show({
  employee,
  daily_reports,
  daily_report_dates,
  user_activity_logs,
  telegram_delivery_logs,
  leave_balances,
  leave_timeline,
  current_user_id,
}) {
  const [tab, setTab] = useState('Daily Reports')

  const roles = useMemo(() => {
    const r = employee?.roles || []
    return r.length ? r : ['user']
  }, [employee])

  const markViewed = (dailyReportId) => {
    router.post(
      `/company_settings/employees/${employee.id}/daily_reports/${dailyReportId}/mark_viewed`,
      {},
      {
        preserveScroll: true,
        onSuccess: () => {
          router.reload({ only: ['daily_reports'], preserveScroll: true, preserveState: true })
        },
      },
    )
  }

  return (
    <>
      <div className="header">
        <img src="/assets/dashkit/covers/profile-cover-1.jpg" className="header-img-top" alt="" />
        <div className="container-fluid">
          <div className="header-body mt-n5 mt-md-n6">
            <div className="row align-items-end">
              <div className="col-auto">
                <div className="avatar avatar-xxl header-avatar-top">
                  <img
                    src={employee.avatar_url}
                    alt={employee.full_name || employee.email}
                    className="avatar-img rounded-circle border border-4 border-body"
                  />
                </div>
              </div>
              <div className="col mb-3 ms-n3 ms-md-n2">
                <h6 className="header-pretitle">Employees</h6>
                <h1 className="header-title">{employee.full_name || employee.email}</h1>
              </div>
              <div className="col-12 col-md-auto mt-2 mt-md-0 mb-md-3">
                <button
                  type="button"
                  className="btn btn-primary d-block d-md-inline-block lift"
                  onClick={() => router.visit(`/company_settings/employees/${employee.id}/edit`)}
                >
                  Edit
                </button>
              </div>
            </div>

            <div className="row align-items-center">
              <div className="col">
                <ul className="nav nav-tabs nav-overflow header-tabs">
                  {[
                    'Daily Reports',
                    'Leaves',
                    'Reimbursements',
                    'Payslips',
                    'Contracts',
                    'Memos',
                    'User Activity',
                    'Telegram Delivery',
                  ].map((t) => (
                    <li key={t} className="nav-item">
                      <button
                        type="button"
                        className={`nav-link ${tab === t ? 'active' : ''}`}
                        onClick={() => setTab(t)}
                      >
                        {t}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-xl-8">
            {tab === 'Daily Reports' ? (
              (daily_reports || []).length ? (
                <div className="d-flex flex-column gap-3">
                  {(daily_reports || []).map((dr) => (
                    <div key={dr.id} id={`daily-report-${dr.applicable_date}`} className="card">
                      <div className="card-body">
                        <div className="mb-3">
                          <div className="row align-items-center">
                            <div className="col-auto">
                              <a href="#" className="avatar" onClick={(e) => e.preventDefault()}>
                                <img
                                  src={employee.avatar_url}
                                  alt={employee.full_name || employee.email}
                                  className="avatar-img rounded-circle"
                                />
                              </a>
                            </div>

                            <div className="col ms-n2">
                              <h4 className="mb-1">{dr.applicable_date_label}</h4>
                              <p className="card-text small text-body-secondary">
                                <span className="fe fe-clock"></span>{' '}
                                <time dateTime={dr.submitted_at}>{dr.submitted_at_label}</time>
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="daily-report-content" dangerouslySetInnerHTML={{ __html: dr.content_html }} />

                        <div className="mb-0">
                          <div className="row">
                            <div className="col"></div>
                            <div className="col-auto me-n3 d-flex align-items-center gap-2">
                              <AvatarGroup users={dr.viewers || []} max={4} />
                              <button
                                type="button"
                                className="btn btn-sm btn-white"
                                disabled={dr.viewed_by_current_user || String(current_user_id) === String(employee.id)}
                                onClick={() => markViewed(dr.id)}
                              >
                                {dr.viewed_by_current_user ? 'Viewed' : 'Mark as viewed'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card">
                  <div className="card-body text-body-secondary">No daily reports yet.</div>
                </div>
              )
            ) : tab === 'Leaves' ? (
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h4 className="card-header-title">Leave History</h4>
                  <button
                    type="button"
                    className="btn btn-sm btn-white"
                    onClick={() => router.post(`/company_settings/employees/${employee.id}/recompute_leave_balances`)}
                  >
                    Recompute balances
                  </button>
                </div>
                <div className="card-body">
                  <div className="d-flex gap-4 mb-3">
                    <div>
                      <div className="text-body-secondary">Sick Leave</div>
                      <div className="h4 mb-0">{formatBalance(leave_balances?.sick)}</div>
                    </div>
                    <div>
                      <div className="text-body-secondary">Vacation Leave</div>
                      <div className="h4 mb-0">{formatBalance(leave_balances?.vacation)}</div>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-sm table-hover table-nowrap">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Entry</th>
                          <th>Type</th>
                          <th>Days</th>
                          <th>Recorded By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(leave_timeline || []).map((item) => (
                          <TimelineRow key={`${item.kind}-${item.id}`} item={item} />
                        ))}
                        {!leave_timeline?.length ? (
                          <tr>
                            <td colSpan={5} className="text-center text-body-secondary py-4">
                              No leave history yet.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : tab === 'User Activity' ? (
              <div className="card card-fill">
                <div className="card-header">
                  <h4 className="card-header-title">User Activity</h4>
                </div>
                <div className="card-body">
                  {(user_activity_logs || []).length ? (
                    <div className="list-group list-group-flush list-group-activity my-n3">
                      {(user_activity_logs || []).map((log) => (
                        <div key={log.id} className="list-group-item">
                          <div className="row">
                            <div className="col-auto">
                              <div className="avatar avatar-sm">
                                <div className="avatar-title fs-lg bg-primary-subtle rounded-circle text-primary">
                                  <i className="fe fe-activity"></i>
                                </div>
                              </div>
                            </div>

                            <div className="col ms-n2">
                              <h5 className="mb-1">{log.title}</h5>
                              <pre className="small text-gray-700 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                <code>{log.payload_pretty}</code>
                              </pre>
                              <small className="text-body-secondary">
                                <time dateTime={log.created_at}>{log.created_at_label}</time>
                              </small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-body-secondary">No activity yet.</div>
                  )}
                </div>
              </div>
            ) : tab === 'Telegram Delivery' ? (
              <div className="card card-fill">
                <div className="card-header">
                  <h4 className="card-header-title">Telegram Delivery</h4>
                </div>
                <div className="card-body">
                  {(telegram_delivery_logs || []).length ? (
                    <div className="list-group list-group-flush list-group-activity my-n3">
                      {(telegram_delivery_logs || []).map((log) => (
                        <div key={log.id} className="list-group-item">
                          <div className="row">
                            <div className="col-auto">
                              <div className="avatar avatar-sm">
                                <div className="avatar-title fs-lg bg-warning-subtle rounded-circle text-warning">
                                  <i className="fe fe-message-circle"></i>
                                </div>
                              </div>
                            </div>

                            <div className="col ms-n2">
                              <h5 className="mb-1">{log.title}</h5>
                              {log.error_message ? <div className="small text-body-secondary mb-2">{log.error_message}</div> : null}
                              <pre className="small text-gray-700 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                <code>{log.payload_pretty}</code>
                              </pre>
                              <small className="text-body-secondary">
                                <time dateTime={log.created_at}>{log.created_at_label}</time>
                              </small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-body-secondary">No telegram delivery logs yet.</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-body text-body-secondary">Coming soon.</div>
              </div>
            )}
          </div>

          <div className="col-12 col-xl-4">
            <div className="card">
              <div className="card-header">
                <h4 className="card-header-title">Attributes</h4>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm table-borderless mb-0">
                    <tbody>
                      <AttributeRow label="Full name" value={employee.full_name || ''} />
                      <AttributeRow label="Email" value={employee.email || ''} />
                      <AttributeRow label="Roles" value={roles.join(', ')} />
                      <AttributeRow label="Phone" value={employee.phone || ''} />
                      <AttributeRow label="Birthday" value={employee.birthday || ''} />
                      <AttributeRow label="Employment status" value={employee.employment_status || ''} />
                      <AttributeRow label="Contract start date" value={employee.contract_start_date || ''} />
                      <AttributeRow label="Contract end date" value={employee.contract_end_date || ''} />
                      <AttributeRow
                        label="Gross salary"
                        value={employee.gross_salary === null || employee.gross_salary === undefined ? '' : String(employee.gross_salary)}
                      />
                      <AttributeRow
                        label="Hourly rate"
                        value={employee.hourly_rate === null || employee.hourly_rate === undefined ? '' : String(employee.hourly_rate)}
                      />
                      <AttributeRow label="Telegram Chat ID" value={employee.tg_chat_id || ''} />
                    </tbody>
                  </table>
                </div>

                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-white"
                    onClick={() => router.visit('/company_settings/employees')}
                  >
                    Back to users list
                  </button>
                </div>
              </div>
            </div>

            {tab === 'Daily Reports' ? (
              <DailyReportCalendar
                dailyReportDates={daily_report_dates}
                dailyReports={daily_reports}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}
